import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NotificationService } from '@core/services/notification.service';
import { PlatformService } from '@core/services/platform.service';
import rulesServiceFactory from '@core/services/rules/factory';
import { RulesBaseService } from '@core/services/rules/rules-base.service';
import { DarkPopover } from '@mynexus/theme/src/scopedtokens';
import { RoutingUser } from '@shared/models/service-line-rule.model';
import { TableConfig } from '@shared/models/table-config.model';
import { cloneDeep, mapValues, union } from 'lodash-es';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterEvent } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  forkJoin,
  fromEvent,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { MultiSelectsWithTooltips, NONE_OPTION, ON_OFF_FILTER, TooltipMessages } from '../constants';

@Component({
  selector: 'app-rules-table',
  templateUrl: './rules-table.component.html',
  styleUrls: ['./rules-table.component.scss'],
  providers: [
    {
      provide: RulesBaseService,
      useFactory: rulesServiceFactory,
      deps: [PlatformService],
    },
  ],
  standalone: false,
})
export class RulesTableComponent implements OnInit {
  @ViewChild(Table, { static: true }) table!: Table;

  // Filter Templates
  @ViewChild('select', { static: true, read: TemplateRef }) selectTemplate!: TemplateRef<any>;
  @ViewChild('multiselect', { static: true, read: TemplateRef }) multiselectTemplate!: TemplateRef<any>;
  @ViewChild('user', { static: true, read: TemplateRef }) userTemplate!: TemplateRef<any>;
  @ViewChild('npi') npiSelect!: MultiSelect;

  private readonly rulesService = inject(RulesBaseService);
  private readonly notificationService = inject(NotificationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);

  rulesForm!: FormGroup;

  get rules(): FormArray<FormGroup> {
    return this.rulesForm.get('rules') as FormArray<FormGroup>;
  }

  // data
  users: RoutingUser[] = [];
  activeUserIds: string[] = [];
  /**
   * Original references mapped to SelectItem[]
   * Used for creating new records where NULL option is NOT allowed
   */
  references!: any;
  editNPIOptions: string[] = [];

  filterTemplates: Record<string, TemplateRef<any>> = {};
  /**
   * Mapped references for filters and new records where NULL option is allowed
   */
  filtersData: Record<string, any> = {
    userId: null,
    isActive: [...ON_OFF_FILTER],
  };

  // Utils
  isLoading = true;
  isSaving = false;
  first = 0;
  totalRecords = 0;

  // Product based table settings
  readonly tableSettings = {
    stateKey: 'rr-rules',
    rowsPerPage: 10,
    dataKey: this.rulesService.uniqueKey,
  };

  productName = this.platformService.currentProduct.value;

  columnConfig: TableConfig = this.platformService.getRulesConfig();

  access = this.rulesService.getTableAccess();

  originalData!: any;

  unsuccessfullySavedRecords!: any;

  private readonly loadQueueData$: Subject<LazyLoadEvent> = new Subject();

  private prevTableState!: LazyLoadEvent;
  private stash: any[] = [];

  get anyDirty() {
    return this.rulesForm?.dirty;
  }

  get userFilterSelectedLabel() {
    if ((this.table.filters['userId'] as any)?.value?.length >= this.users.length) {
      return 'All Active Users';
    }

    return '{0} items selected';
  }

  ngOnInit() {
    this.rulesForm = this.formBuilder.group({
      rules: this.formBuilder.array([]),
    });

    this.rulesForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.platformService.hasUnsavedChanges = this.anyDirty));

    this.filterTemplates = {
      select: this.selectTemplate,
      multiselect: this.multiselectTemplate,
      user: this.userTemplate,
    };

    this.loadQueueData$
      .pipe(
        tap((query) => {
          this.isLoading = true;
          this.prevTableState = query;
        }),
        map(this.preprocessQuery.bind(this)),
        switchMap((query) =>
          this.rulesService.query(query).pipe(
            catchError(() => {
              this.isLoading = false;

              return of({ items: [] });
            }),
            takeUntilDestroyed(this.destroyRef),
          )
        ),
        tap(({ count }) => (this.totalRecords = count)),
        map((data) => data.items),
        tap((data) => {
          this.originalData = cloneDeep(data);
          this.populateQueueToForm(data);
          this.applyChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => (this.isLoading = false),
      });

    this.loadAll();
  }

  async fetchTableData(event: LazyLoadEvent = this.table.createLazyLoadMetadata()) {
    if (this.anyDirty) {
      try {
        await this.platformService.confirmUnsavedChangesNavigation();
      } catch {
        this.setTableState(this.prevTableState);

        return;
      }
    }

    this.loadQueueData$.next(cloneDeep(event));
  }

  addNewRule(index = 0): FormGroup {
    const newControl = this.rulesService.createForm();
    newControl.patchValue({
      lineOfBusinessId: this.platformService.currentLineOfBusiness$.value?.lineOfBusinessId,
    });
    newControl.markAsDirty();
    this.rules.insert(index, newControl);
    this.rulesForm.markAsDirty();
    this.watchRuleStatusChanges(newControl);

    return newControl;
  }

  saveChanges() {
    const modifiedRecords = this.rules.controls.filter((c) => c.dirty);
    const invalidModifiedRecords = modifiedRecords.filter((record) => record.status === 'INVALID');

    if (!modifiedRecords.length) {
      return;
    }

    if (invalidModifiedRecords.length) {
      invalidModifiedRecords.forEach((record) => {
        record.markAllAsTouched();
      });

      this.notificationService.error('validationError');

      return;
    }

    const requests$ = modifiedRecords.map((record) => {
      const data = record.value;
      const id = data[this.rulesService.uniqueKey] || data['temporaryUniqueKey'];
      let request$;

      if (data[this.rulesService.uniqueKey]) {
        request$ = this.rulesService.update(data[this.rulesService.uniqueKey], data);
      } else {
        delete data[this.rulesService.uniqueKey];

        request$ = this.rulesService.add(data).pipe(tap(() => this.totalRecords++));
      }

      // count successful/unsuccessful attempts
      return request$.pipe(
        tap((rule) => this.patchOriginalData(rule)),
        tap((rule: any) => {
          const patch = Object.keys(rule).reduce((acc, key) => {
            if (record.contains(key)) {
              return { ...acc, [key]: rule[key] };
            }

            return acc;
          }, {});

          record.patchValue(patch, { emitEvent: false });
          record.markAsPristine();
          this.platformService.hasUnsavedChanges = this.anyDirty;
        }),
        map(() => ({ success: true })),
        catchError((error: HttpErrorResponse) => {
          this.notificationService.errorMessage(error.message);

          return of({ success: false, error, id });
        })
      );
    });

    this.isSaving = true;

    forkJoin(requests$)
      .pipe(
        switchMap((res) => {
          const successCount = res.filter((i) => i.success).length;

          if (successCount > 0) {
            return this.getReferences().pipe(map(() => res))
          }

          return of(res);
        }),

        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
      next: (res) => {
        const successRequests = res.filter((i) => i.success);
        const successCount = successRequests.length;
        const allSuccess = successCount === res.length;

        if (allSuccess) {
          this.notificationService.success('ruleUpdate.success');
        } else {
          this.notificationService.error('ruleUpdate.error', [successCount, res.length]);
        }

        this.unsuccessfullySavedRecords = res
          .filter((item) => item.success === false && item.error.error.report.code === 'General-136')
          .map((item) => ({
            id: item.id,
            npis: item.error.error.report.violations.map((record: any) => record.message.split(',')).flat(),
          }));
      },
    });
  }

  public cancelChanges() {
    this.rules.controls.forEach((control: AbstractControl, index: number) => {
      // update controls with previous data
      // in case new controls was created (for new rules) we need to remove them
      // Need to include the userWarnings property for CSS rules around invalid controls to persist. See bug #210779
      if (this.originalData[index]) {
        const userWarnings = this.rulesService.getUserWarnings(this.originalData[index]);

        control.patchValue({ ...this.originalData[index], userWarnings }, { emitEvent: false });
      } else {
        this.rules.removeAt(index);
      }
    });

    this.rulesForm.markAsPristine();
    this.rulesForm.markAsUntouched();
    this.platformService.hasUnsavedChanges = false;
    this.unsuccessfullySavedRecords = null;
  }

  updateRuleActiveStatus(control: FormGroup, status: boolean) {
    const isActiveControl = control.controls['isActive'];
    const id = control.controls[this.rulesService.uniqueKey].value;

    // ignore new rules
    if (!id) {
      return;
    }

    // ignore rules with already updated fields
    // already updated field should be saved by save changes action
    // toggle should perform instant update only on untouched controls
    const noOtherChangesDetected = Object.keys(control.controls).every((key) => {
      if (key === 'isActive') {
        return true;
      }

      return control.controls[key].pristine;
    });

    if (noOtherChangesDetected) {
      isActiveControl.disable({ emitEvent: false });

      this.rulesService
        .update(id, control.getRawValue())
        .pipe(
          tap((rule) => this.patchOriginalData(rule)),
          finalize(() => isActiveControl.enable({ emitEvent: false }))
        )
        .subscribe({
          next: ({ user, rowVersion }) => {
            control.patchValue({ user, rowVersion }, { emitEvent: false });
            const term = isActiveControl.value ? 'activated' : 'deactivated';
            this.notificationService.success('ruleActivation.success', [term]);
          },
          error: (error: HttpErrorResponse) => {
            isActiveControl.setValue(!status, { emitEvent: false });
            this.notificationService.errorMessage(error.message);
          },
        });
    }
  }

  async deleteRule(control: FormGroup, index: number) {
    const id = control.controls[this.rulesService.uniqueKey].value;

    if (!id) {
      control.markAsPristine();
      // index on any new page is started not from 0 (using global index of all records)
      // we need to transform it to 0 index to be able to manipulate with rows by local index
      this.rules.removeAt(index - this.first, { emitEvent: false });

      return;
    }

    try {
      await this.notificationService.confirm({
        header: 'Remove Record',
        message: 'Are you sure that you want to remove this record?',
        acceptLabel: 'Remove',
        rejectLabel: 'Cancel',
      });
      this.stashChanges();
      this.rulesService.delete(id).subscribe({
        next: () => {
          this.notificationService.success('ruleDelete.success');
          // bypass unsaved changes check
          this.rulesForm.markAsPristine();
          this.fetchTableData();
        },
        error: (error) => this.notificationService.error(error.message),
      });
    } catch {
      /* empty */
    }
  }

  clearFilters(): void {
    const { sortOrder, sortField, filters, first } = this.getDefaultFiltersState();

    this.table.lazy = false;
    this.table.first = first;
    this.table.filters = filters;
    this.table.sortOrder = sortOrder;
    this.table.sortField = sortField;
    this.table.totalRecords = this.totalRecords;
    this.table.saveState();
    this.table.lazy = true;
    this.table.sortSingle();
  }

  filterByUser(event: MultiSelectChangeEvent, prop: string) {
    const { value } = event.itemValue

    switch (value) {
      case 'inactive':
        if (event.value.includes('inactive')) {
          this.table.filter([value], prop, 'not');
        } else {
          this.table.filter([], prop, 'in');
        }

        break;
      case 'all':
        if (event.value.includes('all')) {
          this.table.filter([value, ...this.activeUserIds], prop, 'in');
        } else {
          this.table.filter([], prop, 'in');
        }

        break;
      default:
        // eslint-disable-next-line no-case-declarations
        const val = event.value.filter((v: string) => !['inactive', 'all'].includes(v));

        if (val.length >= this.activeUserIds.length) {
          val.push('all');
        }

        this.table.filter(val, prop, 'in');
        break;
    }
  }

  isControlInvalid(control: AbstractControl, rule: FormGroup, colunmKey: string) {
    if (!control) {
      return false;
    }

    return (control.touched && control.invalid) || (this.hasError136(rule) && colunmKey === 'npIs');
  }

  getRowIndex(index: number) {
    return index - this.first < 0 ? index : index - this.first;
  }

  onNPISelect(data: MultiSelectChangeEvent) {
    this.editNPIOptions = [...new Set([...this.editNPIOptions, ...data.value])];
  }

  onNpiFilter(event: MultiSelectFilterEvent) {
    if (event.filter.length === 0) {
      this.npiSelect.options = this.editNPIOptions;
    }
  }

  setNPIOptions(data: any) {
    const { npIs } = data.controls;
    const rule =
      this.originalData.find(
        (o: any) => o[this.rulesService.uniqueKey] === data.controls[this.rulesService.uniqueKey].value
      ) ?? [];
    this.editNPIOptions = union(rule.npIs, npIs.value);
  }

  initNPISearch(rule: FormGroup) {
    const MIN_SEARCH_CHARS = 5;
    const event$ = fromEvent(this.npiSelect.filterInputChild?.nativeElement, 'input');

    const tenantNameValue = rule.controls['tenants'].value;
    const emptyMessage = tenantNameValue?.length === 1 ? 'NPI not found for this Client' : 'No results found';

    event$
      .pipe(
        map((data: any) => data.target.value?.trim()),
        tap((data) => {
          if (data.length >= MIN_SEARCH_CHARS) {
            this.npiSelect.emptyFilterMessage = 'Loading...';
          } else {
            this.npiSelect.emptyFilterMessage = 'No results found';
          }

          this.npiSelect.cd.markForCheck();
        }),
        debounceTime(500),
        filter((data: string) => data.length >= MIN_SEARCH_CHARS),
        switchMap((data) => this.rulesService.searchByNpi(data, tenantNameValue)),
        catchError((_, caught) => {
          this.npiSelect.emptyFilterMessage = 'No results found';

          this.npiSelect.cd.markForCheck();

          return caught;
        })
      )
      .subscribe({
        next: (data: any) => {
          this.npiSelect.emptyFilterMessage = emptyMessage;
          this.npiSelect.options = this.removeDuplicates([
            ...this.editNPIOptions,
            ...this.removeDuplicates(Object.values(data)?.flat())
          ]);
          this.npiSelect.cd.markForCheck();
        },
      });
  }

  isInvalidNpi(npi: string, rule: FormGroup): boolean {
    if (!this.unsuccessfullySavedRecords || !rule.dirty) {
      return false;
    } else {
      const currentRule = this.unsuccessfullySavedRecords.find(
        (record: any) =>
          record['id'] === rule.value[this.rulesService.uniqueKey] || record['id'] === rule.value['temporaryUniqueKey']
      );

      return currentRule ? currentRule['npis']?.includes(npi) : false;
    }
  }

  hasError136(rule: FormGroup): boolean {
    return this.unsuccessfullySavedRecords?.find(
      (record: any) =>
        record['id'] === rule.value[this.rulesService.uniqueKey] || record['id'] === rule.value['temporaryUniqueKey']
    );
  }

  getTooltipInMultiSelectOption(option: SelectItem, filter: string): string | null {
    if (!MultiSelectsWithTooltips.includes(filter)) {
      return null;
    }

    return TooltipMessages[option.value];
  }

  /**
   * Load all data for table (including references and list of users)
   * @private
   */
  private loadAll(): void {
    forkJoin([this.getReferences(), this.getUsers()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.setTableState(this.getDefaultFiltersState());
          this.fetchTableData();
        },
      });
  }

  private getUsers(): Observable<void> {
    return this.rulesService.getUsers().pipe(
      tap((users) => (this.users = users)),
      tap((users) => (this.activeUserIds = users.map((user) => user.userId))),
      map((users) => users.map(({ userId, name }) => ({ value: userId, label: name }))),
      tap((users) => {
        this.filtersData['userId'] = [
          {
            label: '',
            items: [
              { label: 'All Active Users', value: 'all' },
              { label: 'Inactive Users', value: 'inactive' },
            ],
          },
          {
            label: '',
            items: [...users],
          },
        ];
      }),
      catchError(() => {
        this.activeUserIds = [];

        return of(null);
      }),
      map(() => undefined)
    );
  }

  private getReferences(): Observable<void> {
    return this.rulesService.getReferences().pipe(
      map((refs) => mapValues(refs, (ref) => ref.map((value) => ({ value, label: value })))),
      tap((refs) => (this.references = refs)),
      map((refs) => mapValues(refs, (ref) => [NONE_OPTION, ...ref])),
      tap((refs) => {
        this.filtersData = { ...this.filtersData, ...refs };
      }),
      catchError(() => {
        this.filtersData = {};

        return of(null);
      }),
      map(() => undefined)
    );
  }

  private preprocessQuery(rawQuery: LazyLoadEvent) {
    if (!rawQuery.filters) {
      return rawQuery;
    }

    const processedQuery: LazyLoadEvent = rawQuery;

    const filters = Object.entries(processedQuery.filters!).reduce((acc, [key, meta]) => {
      if (key === 'userId') {
        if (meta.value.includes('all')) {
          meta.value = [...this.activeUserIds];
        }

        if (meta.value.includes('inactive')) {
          meta.value = [...this.users.map((user) => user.userId)];
        }
      }

      const value = Array.isArray(meta.value) ? meta.value.map((v: any) => v ?? 'null') : meta.value;

      return { ...acc, [key]: { ...meta, value } };
    }, {});

    return { ...processedQuery, filters };
  }

  private populateQueueToForm(data: any) {
    this.rules.clear();
    this.rulesForm.markAsPristine();
    this.rulesForm.markAsUntouched();

    data.forEach((item: any, index: any) => {
      const controls = this.rulesService.createForm();
      const userWarnings = this.rulesService.getUserWarnings(item);

      this.rules.push(controls, { emitEvent: false });
      const control = this.rules.controls[index];
      const isActiveControl = control.get('isActive');

      control.patchValue({ ...item, userWarnings });

      if (isActiveControl && !this.access.update) {
        isActiveControl.disable();
      }

      this.watchRuleStatusChanges(controls);
    });
  }

  private patchOriginalData(rule: any) {
    const index = this.originalData.findIndex(
      (r: any) => r[this.rulesService.uniqueKey] === rule[this.rulesService.uniqueKey]
    );

    if (index > -1) {
      this.originalData[index] = rule;
    } else {
      this.originalData = [rule, ...this.originalData];
    }
  }

  /**
   * Stash updated rules before table reload
   * (used for safe rule delete action)
   * @private
   */
  private stashChanges() {
    this.stash = this.rules.controls
      .filter((c) => c.dirty)
      .map((c: any) => {
        return Object.fromEntries(
          Object.entries(c.controls)
            .filter(([key, value]: any) => key === this.rulesService.uniqueKey || value.dirty)
            .map(([key, value]: any) => [key, value.value])
        );
      });
  }

  /**
   * Apply stashed changes after data refresh
   * @private
   */
  private applyChanges() {
    if (!this.stash.length) {
      return;
    }

    this.stash.forEach((item, index) => {
      let rule: any;

      if (!item[this.rulesService.uniqueKey]) {
        rule = this.addNewRule(index);
      } else {
        rule = this.rules.controls.find(
          (r) => r.value[this.rulesService.uniqueKey] === item[this.rulesService.uniqueKey]
        );
      }

      if (rule) {
        Object.keys(item).forEach((key) => {
          if (key !== this.rulesService.uniqueKey) {
            const control = rule.controls[key];
            rule.controls[key].patchValue(item[key]);
            control.markAsDirty();
          }
        });
      }
    });

    this.stash = [];
  }

  private watchRuleStatusChanges(control: FormGroup) {
    const isActiveControl = control.get('isActive');

    isActiveControl?.valueChanges
      .pipe(
        tap(() => control.get('isActive')?.markAsPristine()),
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => this.updateRuleActiveStatus(control, status));
  }

  private setTableState(state: LazyLoadEvent): void {
    const { first = 0, rows = this.tableSettings.rowsPerPage, sortField = '', sortOrder = 0, filters = {} } = state;

    this.table.lazy = false;
    this.table.filters = filters;
    this.table.sortOrder = sortOrder;
    this.table.sortField = sortField;
    // run cd for table sort components
    this.table.tableService.onSort({
      field: sortField,
      order: sortOrder,
    });
    this.table.first = first;
    this.table.rows = rows;
    this.table.saveState();
    this.table.lazy = true;
  }

  private getDefaultFiltersState() {
    return {
      filters: {
        userId: {
          matchMode: 'in',
          value: ['all', ...this.activeUserIds],
        },
      },
      first: 0,
      globalFilter: null,
      rows: this.tableSettings.rowsPerPage,
      sortField: 'user/name',
      sortOrder: 1,
    };
  }

  private removeDuplicates(userList: any[]): any[] {
    const typesList = userList.reduce((acc: any[], cur: any) => {
      if (cur && !acc.includes(cur)) {
        acc.push(cur);
      }

      return acc;
    }, []);

    return typesList;
  }

  protected readonly DarkPopover = DarkPopover;
}
